/**
 * The rows for ONE topic of the GBP audit checklist.
 *
 * The topic is chosen in the rail beside this (GbpTopicRail), which is a real
 * vertical tablist — so the selected tab IS this panel's heading and the
 * heading is deliberately not repeated here. Repeating it would put the same
 * text on screen twice for anyone reading with a screen reader.
 *
 * Shows EVERY item in the topic, not only the failures. A reader who sees only
 * problems cannot tell a clean profile from a check that never ran, and the
 * person signing this off needs to know which is which.
 *
 * The five states are visually distinct on purpose:
 *   pass        green   — checked, fine
 *   fail        red     — checked, problem found
 *   not checked muted   — we could NOT look. Never rendered as a pass.
 *   needs you   coral   — a person or the client must answer it
 *   n/a         muted   — cannot be automated at all, with the reason shown
 *   declined    teal    — the client said no; suppressed from the counts
 *
 * Per CLAUDE.md, red and green are reserved for status like this; everything
 * else uses the brand coral/teal/muted tokens.
 */
import {
	AlertCircle,
	Ban,
	Check,
	CircleSlash,
	HelpCircle,
	Info,
	Loader2,
	Undo2,
	UserCheck,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import type {
	GbpChecklistItem,
	GbpChecklistStatus,
	GbpEvidenceFact,
} from "@/services/gbpAuditService";

type DecisionStatus = "approved" | "declined" | "reopened" | "flagged";

const STATUS_META: Record<
	GbpChecklistStatus,
	{ label: string; className: string; Icon: typeof Check }
> = {
	pass: {
		// Who checked it matters as much as the verdict: "Passed" alone left
		// people unsure whether a person had signed it off or the tool had.
		label: "Passed by Core",
		className:
			"bg-[hsl(var(--status-success-bg))] text-[hsl(var(--status-success))]",
		Icon: Check,
	},
	fail: {
		label: "Needs fixing",
		className: "bg-destructive/10 text-destructive",
		Icon: AlertCircle,
	},
	// An info finding. Amber, not red: "the link redirects" is worth saying and
	// is not "the website is down", which is what a red row claimed.
	minor: {
		label: "Minor note",
		className: "bg-[hsl(var(--status-warning-bg))] text-[hsl(var(--status-warning))]",
		Icon: Info,
	},
	// Deliberately NOT green and deliberately not silent: we could not look.
	unknown: {
		label: "Not checked",
		className: "bg-muted text-muted-foreground",
		Icon: HelpCircle,
	},
	manual: {
		label: "Needs verification",
		className: "bg-coral-light text-coral-dark",
		Icon: UserCheck,
	},
	unavailable: {
		label: "Can't automate",
		className: "bg-muted text-muted-foreground",
		Icon: CircleSlash,
	},
	confirmed: {
		label: "Passed",
		className:
			"bg-[hsl(var(--status-success-bg))] text-[hsl(var(--status-success))]",
		Icon: UserCheck,
	},
	declined: {
		label: "Declined",
		className: "bg-teal-light text-teal-dark",
		Icon: Ban,
	},
	dismissed: {
		label: "Declined",
		className: "bg-teal-light text-teal-dark",
		Icon: Ban,
	},
} as Record<GbpChecklistStatus, { label: string; className: string; Icon: typeof Check }>;

const fallbackMeta = STATUS_META.unknown;

function StatusPill({ status, decision }: {
	status: GbpChecklistStatus;
	decision?: GbpChecklistItem["decision"];
}) {
	const meta = STATUS_META[status] ?? fallbackMeta;
	const Icon = meta.Icon;
	// A human confirmation is signed. "Passed by Ana" and "Passed by Core" are
	// different claims, and a reader should not have to scroll to tell which
	// one they are looking at.
	const label =
		status === "confirmed" && decision?.status === "approved"
			? `Passed by ${actorName(decision.actor)}`
			: meta.label;
	return (
		<span
			className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${meta.className}`}
		>
			<Icon className="h-3 w-3" />
			{label}
		</span>
	);
}

function formatWhen(value: string | null): string {
	if (!value) return "";
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime())
		? value
		: parsed.toLocaleDateString(undefined, {
				year: "numeric",
				month: "short",
				day: "numeric",
			});
}

/** "user:carl@agileandco.com" reads badly in a sentence. */
function actorName(actor: string): string {
	return (actor || "").replace(/^user:/, "") || "someone";
}

/**
 * What we actually observed, on one line.
 *
 * Deliberately lighter than the failing rows' two-column comparison boxes:
 * a passing row should be checkable at a glance without competing visually
 * with the things that need work, and there can be fifty of them.
 */
function Evidence({ facts }: { facts: GbpEvidenceFact[] }) {
	if (!facts.length) return null;
	return (
		<p className="mt-1 text-xs text-muted-foreground" data-testid="gbp-evidence">
			{facts.map((fact, index) => (
				<span key={fact.label + index}>
					{index > 0 && <span className="mx-1.5 opacity-50">·</span>}
					<span className="uppercase tracking-wide opacity-70">
						{fact.label}
					</span>{" "}
					{fact.href ? (
						<a
							href={fact.href}
							target="_blank"
							rel="noreferrer noopener"
							className="text-coral underline underline-offset-2"
						>
							{fact.value}
						</a>
					) : (
						<span className="text-foreground/80">{fact.value}</span>
					)}
				</span>
			))}
		</p>
	);
}

function ItemRow({ item, onDecide, pending }: {
	item: GbpChecklistItem;
	onDecide: Props["onDecide"];
	pending: boolean;
}) {
	const finding = item.finding;
	const comparable = Boolean(finding?.gbpValue && finding?.siteValue);
	const decided = item.status === "dismissed" || Boolean(item.decision);
	// Green means "this is done", by the tool or by a person. A failing row
	// someone marked confirmed is deliberately NOT tinted — confirming
	// something does not make it true, and the row still says so.
	const settled = item.status === "pass" || item.status === "confirmed";
	// A flag is a request, not a verdict: the row is still waiting on someone,
	// so it must keep offering the sign-off that clears it.
	const flagged = item.decision?.status === "flagged";

	return (
		<div
			className={`border-b border-border px-2 py-3 last:border-0 ${
				settled
					? "bg-[hsl(var(--status-success-bg))]/40"
					: ""
			}`}
		>
			<div className="flex items-start justify-between gap-3">
				<p className="text-sm font-medium">{item.title}</p>
				<StatusPill status={item.status} decision={item.decision} />
			</div>

			{finding?.detail && (
				<p className="mt-1 text-sm text-muted-foreground">{finding.detail}</p>
			)}
			{!finding && item.note && (
				<p className="mt-1 text-sm text-muted-foreground">{item.note}</p>
			)}

			<Evidence facts={item.evidence} />

			{comparable && (
				<div className="mt-2 grid gap-2 sm:grid-cols-2">
					<div className="rounded-md bg-muted/50 p-2">
						<p className="text-[11px] uppercase tracking-wide text-muted-foreground">
							Google
						</p>
						<p className="break-words text-sm">{finding?.gbpValue}</p>
					</div>
					<div className="rounded-md bg-muted/50 p-2">
						<p className="text-[11px] uppercase tracking-wide text-muted-foreground">
							Our records
						</p>
						<p className="break-words text-sm">{finding?.siteValue}</p>
					</div>
				</div>
			)}

			{item.decision && (
				<p className="mt-2 text-xs text-muted-foreground">
					{item.decision.status === "declined"
						? "Declined"
						: item.decision.status === "flagged"
							? "Sent back for verification"
							: "Verified"}{" "}
					by {actorName(item.decision.actor)}
					{item.decision.decidedAt &&
						` on ${formatWhen(item.decision.decidedAt)}`}
					{item.decision.note && ` — “${item.decision.note}”`}
					{/* Confirming something does not make it true: if the check
					    still fails, say so rather than letting the row read as
					    settled. */}
					{item.status === "fail" && item.decision.status === "approved" && (
						<span className="text-destructive">
							{" "}
							— the check still fails.
						</span>
					)}
				</p>
			)}

			{/* Right-aligned: the actions are the same three on every row, so a
			    fixed edge is easier to find than one that moves with the text. */}
			<div className="mt-2 flex justify-end gap-2">
				{pending && (
					/* Was a bare spinning circle with no words, which is
					   indistinguishable from a stuck row. Say what is being
					   waited on. */
					<span
						className="flex items-center gap-1.5 text-xs text-muted-foreground"
						role="status"
						aria-live="polite"
						data-testid="gbp-checklist-saving"
					>
						<Loader2 className="h-3.5 w-3.5 animate-spin" />
						Saving your decision…
					</span>
				)}
				{!pending && decided && (
					<Button
						size="sm"
						variant="ghost"
						onClick={() => onDecide(item.id, "reopened")}
					>
						<Undo2 className="mr-1.5 h-3.5 w-3.5" />
						Reopen
					</Button>
				)}
				{/*
				  * Sending an AUTOMATIC pass back for a look. It has no decision
				  * to withdraw, so Reopen cannot express this — it would just
				  * re-run the same check and pass again. Deliberately not
				  * offered on a row a person already signed: that one is theirs
				  * to Reopen, and two buttons undoing the same thing read as a
				  * choice when there isn't one.
				  */}
				{!pending && item.status === "pass" && (
					<Button
						size="sm"
						variant="ghost"
						className="text-muted-foreground hover:text-foreground"
						onClick={() => onDecide(item.id, "flagged")}
					>
						<UserCheck className="mr-1.5 h-3.5 w-3.5" />
						Needs verification
					</Button>
				)}
				{/*
				  * ANY row not already signed off can be marked passed —
				  * including one that already passes automatically, where it
				  * means "I have eyes on this too" rather than "I fixed it".
				  */}
				{!pending && (!decided || flagged) && (
					<Button
						size="sm"
						variant="outline"
						onClick={() => onDecide(item.id, "approved")}
					>
						<Check className="mr-1.5 h-3.5 w-3.5" />
						Mark passed
					</Button>
				)}
				{!pending && !decided &&
					(item.status === "fail" || item.status === "minor") && (
					<Button
						size="sm"
						variant="ghost"
						onClick={() => onDecide(item.id, "declined")}
					>
						<Ban className="mr-1.5 h-3.5 w-3.5" />
						Client declined
					</Button>
				)}
			</div>
		</div>
	);
}

interface Props {
	items: GbpChecklistItem[];
	onDecide: (itemId: string, status: DecisionStatus) => void;
	pendingItemId?: string | null;
}

export default function GbpChecklistTopic({
	items,
	onDecide,
	pendingItemId,
}: Props) {
	if (items.length === 0) {
		return (
			<p className="py-6 text-sm text-muted-foreground">
				Nothing in this topic.
			</p>
		);
	}

	return (
		<div className="rounded-lg border border-border px-4">
			{items.map((item) => (
				<ItemRow
					key={item.id}
					item={item}
					onDecide={onDecide}
					pending={pendingItemId === item.id}
				/>
			))}
		</div>
	);
}
